import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ProductsSection = styled.section`
  padding: 5rem 0;
  background-color: var(--color-primary);
`;

const ProductsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -10px;
    width: 60px;
    height: 3px;
    background-color: var(--color-accent);
    transform: translateX(-50%);
  }
`;

const SectionSubtitle = styled(motion.span)`
  font-size: 1rem;
  color: var(--color-accent);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  display: block;
`;

const SectionDescription = styled(motion.p)`
  max-width: 700px;
  margin: 0 auto;
  color: var(--color-text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 3rem;
`;

const FilterContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

interface FilterButtonProps {
  active: boolean;
}

const FilterButton = styled.button`
  padding: 0.5rem 1.5rem;
  border: 2px solid ${(props: any) => props.active ? 'var(--color-accent)' : 'var(--color-text-secondary)'};
  background-color: ${(props: any) => props.active ? 'var(--color-accent)' : 'transparent'};
  color: ${(props: any) => props.active ? 'var(--color-primary)' : 'var(--color-text)'};
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 2px;
  
  &:hover {
    background-color: var(--color-accent);
    border-color: var(--color-accent);
    color: var(--color-primary);
  }
`;

const ProductsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
`;

const ProductCard = styled(motion.div)`
  background-color: var(--color-secondary);
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  height: 300px;
  overflow: hidden;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${ProductCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProductTag = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: var(--color-accent);
  color: var(--color-primary);
  padding: 0.3rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 2px;
  text-transform: uppercase;
`;

const ProductContent = styled.div`
  padding: 1.5rem;
`;

const ProductTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ProductDescription = styled.p`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const ProductPrice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Price = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--color-accent);
`;

const PriceLabel = styled.span`
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  text-transform: uppercase;
`;

const Products: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const products = [
    {
      id: 1,
      name: "Terno Slim Preto",
      description: "Terno slim fit em tecido premium, ideal para ocasiões formais.",
      price: "R$ 250,00",
      category: "ternos",
      tag: "Mais Alugado",
      image: "/images/terno-preto.jpg"
    },
    {
      id: 2,
      name: "Terno Azul Marinho",
      description: "Terno em azul marinho com corte moderno e tecido de alta qualidade.",
      price: "R$ 250,00",
      category: "ternos",
      tag: "",
      image: "/images/terno-azul-marinho.jpg"
    },
    {
      id: 3,
      name: "Smoking Preto Clássico",
      description: "Smoking tradicional em preto com lapela em cetim, perfeito para eventos de gala.",
      price: "R$ 690,00",
      category: "smokings",
      tag: "Premium",
      image: "/images/smoking-preto.jpg"
    },
    {
      id: 4,
      name: "Terno Cinza Claro",
      description: "Terno em cinza claro com corte slim e acabamento refinado.",
      price: "R$ 350,00",
      category: "ternos",
      tag: "",
      image: "/images/terno-cinza.jpg"
    },
    {
      id: 5,
      name: "Smoking Azul Royal",
      description: "Smoking em azul royal com detalhes em preto, modelo exclusivo para noivos.",
      price: "R$ 490,00",
      category: "smokings",
      tag: "Exclusivo",
      image: "/images/smoking-azul.jpg"
    },
    {
      id: 6,
      name: "Terno Bege Linho",
      description: "Terno em linho bege, ideal para casamentos diurnos e eventos ao ar livre.",
      price: "R$ 290,00",
      category: "ternos",
      tag: "Verão",
      image: "/images/terno-bege.jpg"
    }
  ];
  
  const filteredProducts = filter === 'all' 
    ? products 
    : products.filter(product => product.category === filter);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
    exit: {
      opacity: 0,
      y: 20,
      transition: { duration: 0.3 },
    },
  };

  return (
    <ProductsSection id="products" ref={ref}>
      <ProductsContainer>
        <SectionHeader>
          <SectionSubtitle
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            Nossa Coleção
          </SectionSubtitle>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Produtos em Destaque
          </SectionTitle>
          <SectionDescription
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Explore nossa seleção de ternos e smokings de alta qualidade, disponíveis para aluguel.
            Temos opções para todos os estilos e ocasiões.
          </SectionDescription>
        </SectionHeader>
        
        <FilterContainer
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <FilterButton 
            active={filter === 'all'} 
            onClick={() => setFilter('all')}
          >
            Todos
          </FilterButton>
          <FilterButton 
            active={filter === 'ternos'} 
            onClick={() => setFilter('ternos')}
          >
            Ternos
          </FilterButton>
          <FilterButton 
            active={filter === 'smokings'} 
            onClick={() => setFilter('smokings')}
          >
            Smokings
          </FilterButton>
        </FilterContainer>
        
        <ProductsGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <AnimatePresence>
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                variants={itemVariants}
                layout
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <ProductImageContainer>
                  <ProductImage src={product.image} alt={product.name} />
                  {product.tag && <ProductTag>{product.tag}</ProductTag>}
                </ProductImageContainer>
                <ProductContent>
                  <ProductTitle>{product.name}</ProductTitle>
                  <ProductDescription>{product.description}</ProductDescription>
                  <ProductPrice>
                    <PriceLabel>Aluguel</PriceLabel>
                    <Price>{product.price}</Price>
                  </ProductPrice>
                </ProductContent>
              </ProductCard>
            ))}
          </AnimatePresence>
        </ProductsGrid>
      </ProductsContainer>
    </ProductsSection>
  );
};

export default Products; 